import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { menuItems } from './menuItems';
import { useLanguageNavigate } from '../../../hooks/useLanguageNavigate';

interface SidebarMenuProps {
  collapsed: boolean;
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({ collapsed }) => {
  const languageNavigate = useLanguageNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <nav className="flex-1 mt-2 mb-2 px-2 flex flex-col gap-1">
      {menuItems.map((item) => {
        const pathWithoutLang = location.pathname.replace(/^\/[^/]+/, '');
        const selected = pathWithoutLang === item.path || location.pathname.endsWith(item.path);
        return (
          <button
            key={item.path}
            onClick={() => languageNavigate(item.path)}
            title={collapsed ? t(item.label) : undefined}
            className={[
              'relative flex items-center min-h-[52px] rounded-[12px] w-full border-0 p-0 cursor-pointer',
              'transition-all duration-300 hover:translate-x-1 hover:text-primary',
              collapsed ? 'justify-center px-2' : 'justify-start px-3',
              selected
                ? [
                    'bg-gradient-to-br from-[rgba(255,107,53,0.1)] to-[rgba(255,120,73,0.05)]',
                    'dark:from-[rgba(255,107,53,0.2)] dark:to-[rgba(255,120,73,0.1)]',
                    'text-primary',
                    'hover:from-[rgba(255,107,53,0.15)] hover:to-[rgba(255,120,73,0.08)]',
                    'dark:hover:from-[rgba(255,107,53,0.25)] dark:hover:to-[rgba(255,120,73,0.15)]',
                  ].join(' ')
                : [
                    'bg-transparent text-content/70',
                    'hover:bg-[rgba(255,107,53,0.08)] dark:hover:bg-[rgba(255,107,53,0.15)]',
                  ].join(' '),
            ].join(' ')}
          >
            {/* Active indicator bar */}
            {selected && (
              <span className="absolute left-0 top-2 bottom-2 w-1 rounded-r bg-gradient-to-b from-primary to-primary-light" />
            )}
            {/* Icon */}
            <span
              className={[
                'flex items-center justify-center flex-shrink-0 text-[1.25rem]',
                'transition-[margin] duration-300 ease-in-out',
                collapsed ? 'mr-0' : 'mr-2.5',
              ].join(' ')}
            >
              {item.icon}
            </span>
            {/* Label */}
            <span
              className={[
                'text-sm font-medium whitespace-nowrap overflow-hidden',
                'transition-all duration-300 ease-in-out',
                // `w-0` when collapsed so the hidden label takes NO horizontal
                // space — otherwise justify-center centres [icon + invisible
                // label] and the icon ends up off-centre ("ikony nie na stred").
                collapsed ? 'w-0 opacity-0 -translate-x-5' : 'opacity-100 translate-x-0',
              ].join(' ')}
            >
              {t(item.label)}
            </span>
          </button>
        );
      })}
    </nav>
  );
};

export default SidebarMenu;
